import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { sourceLabel } from "../features/leads/leadFilterLabels";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { ContactActions } from "../components/ui/ContactActions";
import { Tabs, type TabDef } from "../components/ui/Tabs";
import { useLeadQuery } from "../features/leads/api";
import { useWorkspace } from "../features/workspace/WorkspaceContext";
import { useMembersIndex } from "../features/team/useMembersIndex";
import {
  formatDateTime,
  formatRelative,
  formatFollowUpDate,
  toKolkataDateInput,
  todayKolkataInput,
  isFollowUpOverdue,
} from "../lib/format";
import { ActivityComposer } from "../features/activities/ActivityComposer";
import { ActivityTimeline } from "../features/activities/ActivityTimeline";
import { useActivitiesQuery } from "../features/activities/api";
import { useClaimLead, useTransfersQuery } from "../features/transfers/api";
import { TransferDialog } from "../features/transfers/TransferDialog";
import { StatusChanger } from "../features/leads/StatusChanger";
import { useAuth } from "../features/auth/AuthProvider";
import { useSoftDeleteLead } from "../features/exports/api";
import { useRestoreLead, useSetFollowUp } from "../features/leads/api";

type TabKey = "overview" | "activity" | "transfers";

const TABS: TabDef<TabKey>[] = [
  { key: "overview", label: "Overview" },
  { key: "activity", label: "Activity" },
  { key: "transfers", label: "Transfers" },
];

export default function LeadDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isFromTrash = searchParams.get("deleted") === "1";
  const tabParam = searchParams.get("tab") as TabKey | null;
  const [active, setActive] = useState<TabKey>(
    tabParam && TABS.some((t) => t.key === tabParam) ? tabParam : "overview"
  );

  const { current } = useWorkspace();
  const { user } = useAuth();
  const { resolveName } = useMembersIndex();
  const { data, isLoading, isError, error } = useLeadQuery(
    current?.slug ?? null,
    id,
    { includeDeleted: isFromTrash }
  );
  const { data: actData } = useActivitiesQuery(current?.slug ?? null, id);
  const { data: trData } = useTransfersQuery(current?.slug ?? null, id);
  const claim = useClaimLead(current?.slug ?? null);
  const softDelete = useSoftDeleteLead(current?.slug ?? null);
  const restore = useRestoreLead(current?.slug ?? null);
  const [transferOpen, setTransferOpen] = useState(false);

  if (isLoading) return <div className="page"><div className="skeleton-rows">Loading…</div></div>;
  if (isError) return <div className="page"><div className="form-error">{(error as Error).message}</div></div>;

  const lead = data?.lead;
  if (!lead || !user) return null;

  const isDeleted = !!lead.deletedAt;
  const setTab = (key: TabKey) => {
    setActive(key);
    const next = new URLSearchParams(searchParams);
    next.set("tab", key);
    setSearchParams(next, { replace: true });
  };

  // Action gating
  const canTransfer = user.isSuperadmin || user.role === "admin" || user.role === "manager";
  const isAssignedToMe = lead.assignedTo === user.id;
  // Workspace-level kill switch for sales self-claiming. Managers+ are not
  // affected — they continue to claim/transfer regardless. Defaults to true
  // when the field is not yet present (e.g. stale workspace cache).
  const salesAutoClaim = current?.salesAutoClaim !== false;
  const salesClaimAllowed =
    user.isSuperadmin || user.role !== "sales" || salesAutoClaim;
  const canClaim = !lead.assignedTo && salesClaimAllowed;
  // A logged-in user assigned to a lead should not see Transfer if the only
  // viable target would be themselves. The dialog itself also filters, but
  // hiding the CTA up-front is clearer.
  const showTransfer = canTransfer && !isDeleted;
  const showClaim = canClaim && !isDeleted;
  const showDelete = canTransfer && !isDeleted;

  return (
    <div className="page">
      <div className="breadcrumbs">
        <Link to={isFromTrash ? "/leads/_trash" : "/leads"} className="link">
          {isFromTrash ? "Trash" : "Leads"}
        </Link>
        <span className="sep">/</span>
        <span>{lead.name ?? lead.externalId}</span>
      </div>

      {isDeleted ? (
        <div className="deleted-banner">
          <div>
            <span className="deleted-banner-title">Deleted</span>
            <div className="muted" style={{ marginTop: 2 }}>
              This lead is in the trash. Restore to bring it back into the
              active list.
            </div>
          </div>
          <Button
            variant="primary"
            onClick={async () => {
              await restore.mutateAsync(lead.id);
              navigate(`/leads/${lead.id}`);
            }}
            disabled={restore.isPending}
          >
            {restore.isPending ? "Restoring…" : "Restore"}
          </Button>
        </div>
      ) : null}

      <header className="page-header">
        <div>
          <h1 className="page-title">{lead.name ?? "(no name)"}</h1>
          <div className="page-meta">
            {isDeleted ? (
              <span className="badge">Status: {lead.status.replace("_", " ")}</span>
            ) : (
              <StatusChanger
                workspaceSlug={current?.slug ?? null}
                leadId={lead.id}
                status={lead.status}
              />
            )}
            {lead.assignedTo ? (
              <span className="assignee-pill">
                <span className="assignee-pill-label">Assigned to</span>
                <span className="assignee-pill-name">
                  {resolveName(lead.assignedTo)}
                  {isAssignedToMe ? " (you)" : ""}
                </span>
              </span>
            ) : (
              <span className="assignee-pill assignee-pill-unassigned">
                Unassigned
              </span>
            )}
            <span className="badge">{sourceLabel(lead.source)}</span>
            <span className="muted" title={lead.createdAt}>Received {formatRelative(lead.createdAt)}</span>
          </div>
        </div>
        {!isDeleted ? (
          <div className="header-actions">
            {showClaim ? (
              <Button
                variant="secondary"
                onClick={() => claim.mutate({ leadId: lead.id })}
                disabled={claim.isPending}
              >
                {claim.isPending ? "Claiming…" : "Claim"}
              </Button>
            ) : null}
            {showTransfer ? (
              <Button variant="secondary" onClick={() => setTransferOpen(true)}>Transfer</Button>
            ) : null}
            {showDelete ? (
              <Button
                variant="danger"
                onClick={async () => {
                  if (!window.confirm("Move this lead to trash?")) return;
                  await softDelete.mutateAsync(lead.id);
                  navigate("/leads");
                }}
                disabled={softDelete.isPending}
              >
                {softDelete.isPending ? "Deleting…" : "Delete"}
              </Button>
            ) : null}
          </div>
        ) : null}
      </header>

      <Tabs tabs={TABS} active={active} onChange={setTab} />

      {active === "overview" && (
        <div className="tab-panel">
          <div className="detail-grid">
            <Card title="Contact">
              {!isDeleted && (lead.phoneE164 || lead.email) ? (
                <div className="contact-actions-bar">
                  <ContactActions
                    phone={lead.phoneE164}
                    email={lead.email}
                    variant="inline"
                    name={lead.name}
                  />
                </div>
              ) : null}
              <dl className="detail-list">
                <Row label="Email">
                  {lead.email ? <a className="link" href={`mailto:${lead.email}`}>{lead.email}</a> : "—"}
                </Row>
                <Row label="Phone">
                  {lead.phoneE164 ? <a className="link" href={`tel:${lead.phoneE164}`}>{lead.phoneE164}</a> : "—"}
                </Row>
                <Row label="Company">{lead.company ?? "—"}</Row>
                <Row label="City">{lead.city ?? "—"}</Row>
                <Row label="Assigned to">
                  {lead.assignedTo
                    ? `${resolveName(lead.assignedTo)}${isAssignedToMe ? " (you)" : ""}`
                    : "Unassigned"}
                </Row>
                <Row label="Assigned on">
                  {lead.assignedAt ? formatDateTime(lead.assignedAt) : "—"}
                </Row>
                <Row label="Last call">
                  {lead.lastCall
                    ? `${formatDateTime(lead.lastCall.occurredAt)}${
                        lead.lastCall.outcome ? ` · ${lead.lastCall.outcome}` : ""
                      }`
                    : "—"}
                </Row>
                <Row label="Next follow-up">
                  {isDeleted ? (
                    lead.nextFollowUpAt ? (
                      formatFollowUpDate(lead.nextFollowUpAt)
                    ) : (
                      "—"
                    )
                  ) : (
                    <FollowUpControl
                      workspaceSlug={current?.slug ?? null}
                      leadId={lead.id}
                      current={lead.nextFollowUpAt}
                    />
                  )}
                </Row>
              </dl>
            </Card>

            <Card title="Source">
              <dl className="detail-list">
                <Row label="Source">{sourceLabel(lead.source)}</Row>
                <Row label="Campaign">{lead.sourceCampaignName ?? "—"}</Row>
                <Row label="Ad">{lead.sourceAdName ?? "—"}</Row>
                <Row label="Form ID">{lead.sourceFormId ?? "—"}</Row>
                <Row label="External ID">{lead.externalId}</Row>
                <Row label="Submitted">{formatDateTime(lead.submittedAt)}</Row>
              </dl>
            </Card>
          </div>

          {lead.rawFields && Object.keys(lead.rawFields).length > 0 ? (
            <details className="raw-fields" open>
              <summary>Raw form data</summary>
              <div className="raw-fields-body">
                <RawFieldsTable data={lead.rawFields as Record<string, unknown>} />
              </div>
            </details>
          ) : null}
        </div>
      )}

      {active === "activity" && (
        <div className="tab-panel">
          {!isDeleted ? (
            <Card title="Log activity">
              <ActivityComposer
                workspaceSlug={current?.slug ?? null}
                leadId={lead.id}
                currentFollowUpAt={lead.nextFollowUpAt}
              />
            </Card>
          ) : null}
          <Card title="Timeline">
            <ActivityTimeline activities={actData?.activities ?? []} />
          </Card>
        </div>
      )}

      {active === "transfers" && (
        <div className="tab-panel">
          <Card title="Transfer history">
            {(trData?.transfers.length ?? 0) === 0 ? (
              <p className="muted">No transfers recorded for this lead.</p>
            ) : (
              <ol className="timeline">
                {(trData?.transfers ?? []).map((t) => (
                  <li key={t.id} className="timeline-item">
                    <span className="timeline-icon">↔</span>
                    <div className="timeline-content">
                      <div className="timeline-head">
                        <span className="timeline-type">{t.fromUserId ? "Reassigned" : "Assigned"}</span>
                        <span className="timeline-time" title={t.createdAt}>{formatRelative(t.createdAt)}</span>
                      </div>
                      <div className="timeline-body muted">
                        {(t.fromUserId ? resolveName(t.fromUserId) : "Unassigned")} → {resolveName(t.toUserId)}
                        {t.reason ? ` · ${t.reason}` : ""}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </Card>
        </div>
      )}

      {transferOpen && current ? (
        <TransferDialog
          workspaceSlug={current.slug}
          workspaceId={current.id}
          leadId={lead.id}
          currentAssigneeId={lead.assignedTo}
          onClose={() => setTransferOpen(false)}
          onTransferred={() => setTransferOpen(false)}
        />
      ) : null}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </>
  );
}

// Inline set/clear of the lead's next follow-up (direct-edit path). Shows the
// current date (overdue-flagged) with an Edit affordance; editing reveals a
// date picker that saves via PATCH /leads/:id/follow-up.
function FollowUpControl({
  workspaceSlug,
  leadId,
  current,
}: {
  workspaceSlug: string | null;
  leadId: string;
  current: string | null;
}) {
  const setFollowUp = useSetFollowUp(workspaceSlug);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(toKolkataDateInput(current));

  const startEditing = () => {
    setValue(toKolkataDateInput(current));
    setEditing(true);
  };

  const save = async (next: string | null) => {
    await setFollowUp.mutateAsync({ leadId, followUpAt: next });
    setEditing(false);
  };

  if (editing) {
    return (
      <span className="followup-edit">
        <input
          type="date"
          value={value}
          min={todayKolkataInput()}
          onChange={(e) => setValue(e.target.value)}
          aria-label="Follow-up date"
        />
        <Button
          size="sm"
          variant="primary"
          onClick={() => save(value || null)}
          disabled={setFollowUp.isPending || !value}
        >
          Save
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
          Cancel
        </Button>
      </span>
    );
  }

  return (
    <span className="followup-view">
      {current ? (
        <span
          className={
            isFollowUpOverdue(current)
              ? "followup-due followup-overdue"
              : "followup-due"
          }
          title={current}
        >
          {formatFollowUpDate(current)}
        </span>
      ) : (
        <span className="muted">None</span>
      )}
      <button type="button" className="link followup-edit-trigger" onClick={startEditing}>
        {current ? "Edit" : "Set"}
      </button>
      {current ? (
        <button
          type="button"
          className="link followup-clear-trigger"
          onClick={() => save(null)}
          disabled={setFollowUp.isPending}
        >
          Clear
        </button>
      ) : null}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Raw form data table (up to 2 levels deep)
// ---------------------------------------------------------------------------

function RawFieldsTable({ data }: { data: Record<string, unknown> }) {
  return (
    <table className="rft">
      <tbody>
        {Object.entries(data).map(([key, value]) => (
          <RawFieldRow key={key} fieldKey={key} value={value} />
        ))}
      </tbody>
    </table>
  );
}

function RawFieldRow({ fieldKey, value }: { fieldKey: string; value: unknown }) {
  const isNested = value !== null && typeof value === "object";
  return (
    <tr className="rft-row">
      <td className="rft-key">{fieldKey}</td>
      <td className="rft-val">
        {isNested ? (
          <NestedFieldTable value={value as Record<string, unknown> | unknown[]} />
        ) : (
          renderPrimitive(value)
        )}
      </td>
    </tr>
  );
}

function NestedFieldTable({ value }: { value: Record<string, unknown> | unknown[] }) {
  const entries: [string, unknown][] = Array.isArray(value)
    ? value.map((v, i) => [String(i), v])
    : Object.entries(value);

  return (
    <table className="rft rft--nested">
      <tbody>
        {entries.map(([k, v]) => {
          const isDeep = v !== null && typeof v === "object";
          return (
            <tr key={k} className="rft-row">
              <td className="rft-key">{k}</td>
              <td className="rft-val">
                {isDeep ? (
                  <pre className="rft-deep">{JSON.stringify(v, null, 2)}</pre>
                ) : (
                  renderPrimitive(v)
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function renderPrimitive(value: unknown): React.ReactNode {
  if (value === null || value === undefined || value === "") return <span className="rft-empty">—</span>;
  if (typeof value === "boolean") return <span className="rft-bool">{value ? "true" : "false"}</span>;
  return String(value);
}
